import React, { useState } from 'react';
import { Share2, Facebook, Twitter, Linkedin, MessageCircle, Link2, Check } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/**
 * ShareButton Component
 * 
 * Generates shareable links that use /api/share/* endpoints for proper meta tags.
 * Social crawlers will see dynamic og:title, og:description, og:image.
 * 
 * @param {string} type - 'event' | 'speaker' | 'edition'
 * @param {string} slug - The slug of the resource
 * @param {string} title - Optional title for the share (used in share text)
 */
const ShareButton = ({ type, slug, title = '' }) => {
  const [copied, setCopied] = useState(false);
  
  // Generate the share URL using the API endpoint for proper meta tags
  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/api/share/${type}/${slug}`;
  };
  
  const shareUrl = getShareUrl();
  const shareText = title || 'Scopri questo contenuto su SIOS';
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  const handleShare = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };
  
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
  };
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="gap-2"
          data-testid="share-button"
        >
          <Share2 className="h-4 w-4" />
          <span className="hidden sm:inline">Condividi</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem 
          onClick={() => handleShare('facebook')}
          className="cursor-pointer"
          data-testid="share-facebook"
        >
          <Facebook className="h-4 w-4 mr-2 text-blue-600" />
          Facebook
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleShare('twitter')}
          className="cursor-pointer"
          data-testid="share-twitter"
        >
          <Twitter className="h-4 w-4 mr-2 text-sky-500" />
          Twitter / X
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleShare('linkedin')}
          className="cursor-pointer"
          data-testid="share-linkedin"
        >
          <Linkedin className="h-4 w-4 mr-2 text-blue-700" />
          LinkedIn
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleShare('whatsapp')}
          className="cursor-pointer"
          data-testid="share-whatsapp"
        >
          <MessageCircle className="h-4 w-4 mr-2 text-green-500" />
          WhatsApp
        </DropdownMenuItem>
        {navigator.share && (
          <DropdownMenuItem 
            onClick={handleNativeShare}
            className="cursor-pointer"
            data-testid="share-native"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Altro...
          </DropdownMenuItem>
        )}
        <DropdownMenuItem 
          onClick={handleCopyLink}
          className="cursor-pointer"
          data-testid="share-copy"
        >
          {copied ? (
            <>
              <Check className="h-4 w-4 mr-2 text-green-500" />
              Copiato!
            </>
          ) : (
            <>
              <Link2 className="h-4 w-4 mr-2" />
              Copia link
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ShareButton;
